import React from 'react';
import { Box, Typography } from '@mui/material';

const RiskMatrix = ({ selectedLikelihood, selectedConsequence, title = "Risk Matrix" }) => {
  const likelihoods = [
    { id: 1, label: 'Rare' },
    { id: 2, label: 'Unlikely' },
    { id: 3, label: 'Possible' },
    { id: 4, label: 'Likely' },
    { id: 5, label: 'Almost Certain' }
  ];

  const consequences = [
    { id: 5, label: 'Catastrophic' },
    { id: 4, label: 'Major' },
    { id: 3, label: 'Moderate' },
    { id: 2, label: 'Minor' },
    { id: 1, label: 'Insignificant' }
  ];

  const matrix = {
    5: ['TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME', 'EXTREME'],
    4: ['LOW', 'TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME'],
    3: ['LOW', 'LOW', 'TOLERABLE', 'HIGH', 'HIGH'],
    2: ['VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE', 'HIGH'],
    1: ['VERY LOW', 'VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE']
  };

  const colors = {
    'EXTREME': '#ef4444',
    'HIGH': '#f97316',
    'TOLERABLE': '#eab308',
    'LOW': '#84cc16',
    'VERY LOW': '#15803d'
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '500px', p: 2 }}>
      <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 2, textAlign: 'center', fontWeight: 'bold' }}>{title}</Typography>
      
      <Box sx={{ position: 'relative', display: 'flex' }}>
        {/* Y-Axis Label (Consequence) */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          mr: 1, 
          position: 'relative'
        }}>
          <Typography sx={{ 
            writingMode: 'vertical-rl', 
            transform: 'rotate(180deg)', 
            color: '#94a3b8', 
            fontSize: '0.7rem', 
            fontWeight: 'bold',
            letterSpacing: '1px'
          }}>
            CONSEQUENCE
          </Typography>
          {/* Arrow Up */}
          <Box sx={{ 
            position: 'absolute', 
            top: -10, 
            left: '50%', 
            transform: 'translateX(-50%)',
            width: 2, 
            height: '100%', 
            background: 'linear-gradient(to top, transparent, #3b82f6)',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: -3,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderBottom: '6px solid #3b82f6'
            }
          }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '80px repeat(5, 1fr)', 
            gap: 0.5 
          }}>
            {/* Rows */}
            {consequences.map((cons) => (
              <React.Fragment key={cons.id}>
                {/* Row Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.65rem', textAlign: 'right', width: '100%' }}>{cons.label}</Typography>
                </Box>
                {/* Cells */}
                {likelihoods.map((lik) => {
                  const label = matrix[cons.id][lik.id - 1];
                  const isSelected = 
                    (String(selectedLikelihood).toUpperCase() === lik.label.toUpperCase()) && 
                    (String(selectedConsequence).toUpperCase() === cons.label.toUpperCase());
                  return (
                    <Box 
                      key={`${cons.id}-${lik.id}`}
                      sx={{ 
                        height: '40px',
                        background: colors[label],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        opacity: isSelected ? 1 : 0.4,
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                        transition: 'all 0.3s ease',
                        border: isSelected ? '2px solid white' : 'none',
                        boxShadow: isSelected ? `0 0 15px ${colors[label]}` : 'none',
                        zIndex: isSelected ? 2 : 1
                      }}
                    >
                      {isSelected && (
                        <Typography sx={{ color: 'white', fontSize: '0.5rem', fontWeight: '900', textAlign: 'center' }}>
                          {label}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}

            {/* Empty corner */}
            <Box />
            {/* Column Headers (Likelihood) */}
            {likelihoods.map((lik) => (
              <Box key={lik.id} sx={{ pt: 1, textAlign: 'center' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>{lik.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* X-Axis Label (Likelihood) */}
          <Box sx={{ mt: 1, position: 'relative', width: 'calc(100% - 80px)', ml: '80px' }}>
            <Typography sx={{ 
              color: '#94a3b8', 
              fontSize: '0.7rem', 
              fontWeight: 'bold', 
              textAlign: 'center',
              letterSpacing: '1px'
            }}>
              LIKELIHOOD
            </Typography>
            {/* Arrow Right */}
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: 0,
              width: '100%', 
              height: 2, 
              background: 'linear-gradient(to right, transparent, #3b82f6)',
              transform: 'translateY(-50%)',
              '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: -3,
                borderTop: '4px solid transparent',
                borderBottom: '4px solid transparent',
                borderLeft: '6px solid #3b82f6'
              }
            }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RiskMatrix;
